import React, { useEffect, useState } from "react";
import { 
  Box, 
  Modal,
  IconButton, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableRow, 
  Typography,
  Avatar,
  Chip,
  LinearProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import {
  Description as FileIcon,
  Person as UserIcon,
  LocationOn as LocationIcon,
  Note as RemarkIcon,
  CheckCircle as AvailableIcon,
  Cancel as IssuedIcon
} from "@mui/icons-material";
import api from "../../../utils/Url";
import { useTranslation } from "react-i18next"; // Add this import

// Define interfaces
interface LocationOption {
  label: string;
  value: number;
}

interface UserOption {
  label: string;
  value: string;
}

interface FileData {
  fileID: string;
  fileName: string;
  fileNo: string;
  rfid: string;
  synopsis: string;
  chackNo: string;
  totalPages: number;
  fromPage: number;
  toPage: number;
  status: string;
  fileTypeName: string;
  fileGroupName: string;
  fileCategoryName: string;
  className: string;
  nodeName: string;
  fileSubjectName: string;
  senderID: string;
  receiverID: string;
  currentLocation: number;
  remark: string;
  fileDoc: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  userData: FileData[];
}

// Premium color palette
const colors = {
  primary: "#4a148c",
  secondary: "#ff6f00",
  background: "#f5f5f7",
  textPrimary: "#212121",
  textSecondary: "#757575",
  success: "#2e7d32",
  error: "#d32f2f",
  info: "#0288d1"
};

// Enhanced styled components
const PremiumTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: colors.primary,
    color: theme.palette.common.white,
    fontWeight: 700,
    fontSize: '0.875rem',
    padding: '8px 12px'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '0.875rem',
    color: colors.textPrimary,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: '8px 12px'
  },
}));

const PremiumTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  '&:last-child td': {
    borderBottom: 'none',
  },
}));

const HeaderTypography = styled(Typography)({
  fontWeight: 600,
  color: colors.primary,
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '0.875rem'
});

const ValueTypography = styled(Typography)({
  color: colors.textPrimary,
  fontWeight: 500,
  fontSize: '0.875rem'
});

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95vw', sm: '80vw', md: '70vw', lg: '60vw' },
  maxHeight: '90vh',
  bgcolor: colors.background,
  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  borderRadius: 2,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column'
};

const FileDetailModal: React.FC<Props> = ({ open, onClose, title, userData }) => {
  const { t } = useTranslation(); // Add this hook
  const [locationOptions, setLocationOptions] = useState<LocationOption[]>([]);
  const [loading, setLoading] = useState({
    locations: true,
    users: true
  });
  const [senderOptions, setSenderOptions] = useState<UserOption[]>([]);
  const [receiverOptions, setReceiverOptions] = useState<UserOption[]>([]);

  const handleClose = () => {
    onClose();
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [locationRes, userRes] = await Promise.all([
          api.post(`LocationItems/GetMappedLocation`, { id: -1 }),
          api.post(`USER/GetUSER`, { useR_ID: "-1" })
        ]);

        setLocationOptions(locationRes.data.data.map((item: any) => ({
          label: item.location,
          value: item.id,
        })));

        const users = userRes.data.data.map((item: any) => ({
          label: `${item.firsT_NAME} ${item.middlE_NAME} ${item.suR_NAME}`.trim(),
          value: item.useR_ID,
        }));
        setSenderOptions(users);
        setReceiverOptions(users);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading({ locations: false, users: false });
      }
    };

    if (open) fetchData();
  }, [open]);

  const getLocationName = (locationId: number): string => {
    if (loading.locations) return t("text.Loading");
    const location = locationOptions.find(opt => opt.value == locationId);
    return location ? location.label : `${t("text.LocationID")}: ${locationId}`;
  };

  const getUserName = (userId: string): string => {
    if (loading.users) return t("text.Loading");
    if (!userId) return t("text.NotSpecified");
    const user = [...senderOptions, ...receiverOptions].find(opt => opt.value == userId);
    return user ? user.label : `${t("text.UserID")}: ${userId}`;
  };

  const renderFileStatus = (status: string) => {
    switch (status) {
      case "2":
        return (
          <Chip 
            icon={<AvailableIcon fontSize="small" />} 
            label={t("text.Issued")} 
            color="success" 
            size="small" 
            variant="outlined"
            sx={{ height: 24 }}
          />
        );
      default:
        return (
          <Chip 
            icon={<AvailableIcon fontSize="small" />} 
            label={t("text.Available")} 
            color="success" 
            size="small" 
            variant="outlined"
            sx={{ height: 24 }}
          />
        );
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="file-detail-modal"
      aria-describedby="file-details"
      sx={{ zIndex: 1300 }}
    >
      <Box sx={modalStyle}>
        <Box sx={{ 
          background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
          color: 'white',
          p: 2,
          position: 'relative'
        }}>
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar sx={{ 
              bgcolor: 'white',
              color: colors.primary,
              width: 40,
              height: 40
            }}>
              <FileIcon fontSize="medium" />
            </Avatar>
            <Typography variant="h6" fontWeight="600">
              {title}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ 
          p: 2, 
          overflowY: 'auto',
          flex: 1,
          '&::-webkit-scrollbar': {
            width: '6px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors.primary,
            borderRadius: '3px'
          }
        }}>
          {loading.locations || loading.users ? (
            <LinearProgress color="secondary" />
          ) : (
            <>
              <Paper elevation={0} sx={{ 
                mb: 2, 
                borderRadius: 1,
                overflow: 'hidden',
                border: `1px solid rgba(0,0,0,0.1)`
              }}>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      {userData?.map((row: FileData) => (
                        <React.Fragment key={row.fileID}>
                          <PremiumTableRow>
                            <PremiumTableCell colSpan={2}>
                              <HeaderTypography>
                                <FileIcon color="primary" fontSize="small" />
                                {t("text.FileDetails")}
                              </HeaderTypography>
                            </PremiumTableCell>
                          </PremiumTableRow>

                          <PremiumTableRow>
                            <PremiumTableCell>
                              <HeaderTypography>{t("text.FileName")}</HeaderTypography>
                              <ValueTypography>{row.fileName || t("text.NA")}</ValueTypography>
                            </PremiumTableCell>
                            <PremiumTableCell>
                              <HeaderTypography>{t("text.FileNumber")}</HeaderTypography>
                              <ValueTypography>{row.fileNo || t("text.NA")}</ValueTypography>
                            </PremiumTableCell>
                          </PremiumTableRow>

                          <PremiumTableRow>
                            <PremiumTableCell>
                              <HeaderTypography>{t("text.RFID")}</HeaderTypography>
                              <ValueTypography>{row.rfid || t("text.NA")}</ValueTypography>
                            </PremiumTableCell>
                            <PremiumTableCell>
                              <HeaderTypography>{t("text.Status")}</HeaderTypography>
                              {renderFileStatus(row.status)}
                            </PremiumTableCell>
                          </PremiumTableRow>

                          <PremiumTableRow>
                            <PremiumTableCell>
                              <HeaderTypography>{t("text.Synopsis")}</HeaderTypography>
                              <ValueTypography>{row.synopsis || t("text.NA")}</ValueTypography>
                            </PremiumTableCell>
                            <PremiumTableCell>
                              <HeaderTypography>{t("text.ChakNo")}</HeaderTypography>
                              <ValueTypography>{row.chackNo || t("text.NA")}</ValueTypography>
                            </PremiumTableCell>
                          </PremiumTableRow>

                          <PremiumTableRow>
                            <PremiumTableCell>
                              <HeaderTypography>{t("text.TotalPages")}</HeaderTypography>
                              <ValueTypography>{row.totalPages || t("text.NA")}</ValueTypography>
                            </PremiumTableCell>
                            <PremiumTableCell>
                              <HeaderTypography>{t("text.PagesRange")}</HeaderTypography>
                              <ValueTypography>
                                {row.fromPage || '0'} - {row.toPage || '0'}
                              </ValueTypography>
                            </PremiumTableCell>
                          </PremiumTableRow>

                          <PremiumTableRow>
                            <PremiumTableCell>
                              <HeaderTypography>{t("text.FileType")}</HeaderTypography>
                              <ValueTypography>{row.fileTypeName || t("text.NA")}</ValueTypography>
                            </PremiumTableCell>
                            <PremiumTableCell>
                              <HeaderTypography>{t("text.FileGroup")}</HeaderTypography>
                              <ValueTypography>{row.fileGroupName || t("text.NA")}</ValueTypography>
                            </PremiumTableCell>
                          </PremiumTableRow>

                          <PremiumTableRow>
                            <PremiumTableCell>
                              <HeaderTypography>{t("text.FileCategory")}</HeaderTypography>
                              <ValueTypography>{row.fileCategoryName || t("text.NA")}</ValueTypography>
                            </PremiumTableCell>
                            <PremiumTableCell>
                              <HeaderTypography>{t("text.FileClass")}</HeaderTypography>
                              <ValueTypography>{row.className || t("text.NA")}</ValueTypography>
                            </PremiumTableCell>
                          </PremiumTableRow>

                          <PremiumTableRow>
                            <PremiumTableCell>
                              <HeaderTypography>
                                <UserIcon color="primary" fontSize="small" />
                                {t("text.Sender")}
                              </HeaderTypography>
                              <ValueTypography>
                                {getUserName(row.senderID)}
                              </ValueTypography>
                            </PremiumTableCell>
                            <PremiumTableCell>
                              <HeaderTypography>
                                <UserIcon color="primary" fontSize="small" />
                                {t("text.Receiver")}
                              </HeaderTypography>
                              <ValueTypography>
                                {getUserName(row.receiverID)}
                              </ValueTypography>
                            </PremiumTableCell>
                          </PremiumTableRow>

                          <PremiumTableRow>
                            <PremiumTableCell colSpan={2}>
                              <HeaderTypography>
                                <LocationIcon color="primary" fontSize="small" />
                                {t("text.Location")}
                              </HeaderTypography>
                              <ValueTypography>
                                {getLocationName(row.currentLocation)}
                              </ValueTypography>
                            </PremiumTableCell>
                          </PremiumTableRow>

                          <PremiumTableRow>
                            <PremiumTableCell colSpan={2}>
                              <HeaderTypography>
                                <RemarkIcon color="primary" fontSize="small" />
                                {t("text.Remark")}
                              </HeaderTypography>
                              <ValueTypography>
                                {row.remark || t("text.NA")}
                              </ValueTypography>
                            </PremiumTableCell>
                          </PremiumTableRow>
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {userData?.length > 0 && userData[0].fileDoc && (
                <Paper elevation={0} sx={{ 
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: `1px solid rgba(0,0,0,0.1)`
                }}>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {userData.map((row: FileData) => (
                          <PremiumTableRow key={`file-${row.fileID}`}>
                            <PremiumTableCell align="center">
                              <HeaderTypography>{t("text.PreviewFile")}</HeaderTypography>
                              {row.fileDoc ? (
                                <Box sx={{ 
                                  border: `1px solid ${colors.textSecondary}`,
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  height: '40vh',
                                  position: 'relative'
                                }}>
                                  <embed
                                    src={row.fileDoc}
                                    style={{ 
                                      width: "100%", 
                                      height: "100%",
                                    }}
                                    title={`${t("text.FileDocument")}: ${row.fileName}`}
                                  />
                                  <Box sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                                    p: 0.5,
                                    textAlign: 'center'
                                  }}>
                                    <Typography variant="caption" color="white">
                                      {row.fileName}
                                    </Typography>
                                  </Box>
                                </Box>
                              ) : (
                                <Typography variant="body2" color="textSecondary">
                                  {t("text.NoFileDocumentAvailable")}
                                </Typography>
                              )}
                            </PremiumTableCell>
                          </PremiumTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default FileDetailModal;